import clsx from "clsx";
import React, {
  ChangeEvent,
  FocusEvent,
  KeyboardEvent,
  useCallback,
  useMemo,
  useRef,
} from "react";

import { FieldError } from "../FieldError";
import { useFocusWithin } from "../hooks";
import { Typo } from "../Typo";

export type DateInputProps = {
  name: string;
  value: string;
  label?: string;
  subLabel?: string;
  disabled?: boolean;
  error?: string;
  className?: string;
  dayPlaceholder?: string;
  monthPlaceholder?: string;
  yearPlaceholder?: string;
  separator?: string;
  required?: boolean;
  onChange: (value: string) => void;
  onFocus?: () => void;
};

export const DateInput = ({
  name,
  label,
  subLabel,
  value,
  disabled = false,
  error,
  className,
  dayPlaceholder = "DD",
  monthPlaceholder = "MM",
  yearPlaceholder = "YYYY",
  separator = "/",
  required = false,
  onChange,
  onFocus,
}: DateInputProps) => {
  const inputs = useRef<(HTMLInputElement | null)[]>([]);
  const [focusedInput, setFocusedInput] = React.useState<string>();

  const moveToNext = (current: HTMLInputElement) => {
    if (current.selectionStart === current.maxLength) {
      const index = inputs.current.indexOf(current);

      if (index < inputs.current.length - 1) {
        const next = inputs.current[index + 1];
        if (next) next.focus();
      }
    }
  };

  const moveToPrevious = (current: HTMLInputElement) => {
    if (current.selectionStart === 0 && current.selectionEnd === 0) {
      const index = inputs.current.indexOf(current);

      if (index > 0) {
        const previous = inputs.current[index - 1];

        if (previous) {
          previous.focus();

          setTimeout(() =>
            previous.setSelectionRange(previous.maxLength, previous.maxLength),
          );
        }
      }
    }
  };

  const [year = "", month = "", day = ""] = useMemo(() => {
    return value.split("-");
  }, [value]);

  const focusFirstEmptyInput = () => {
    if (!day) {
      inputs.current[0]?.focus();
      setFocusedInput("day");
      return;
    }

    if (!month) {
      inputs.current[1]?.focus();
      setFocusedInput("month");
      return;
    }

    if (!year) {
      inputs.current[2]?.focus();
      setFocusedInput("year");
      return;
    }
  };

  const checkIfInputIsComplete = () => !!(day && month && year);

  const handleInputFocus =
    (component: string) => (e: FocusEvent<HTMLInputElement>) => {
      if (checkIfInputIsComplete()) {
        setFocusedInput(component);
      } else {
        if (focusedInput) {
          setFocusedInput(component);
        } else {
          focusFirstEmptyInput();
        }
      }

      const input = e.target;
      setTimeout(() => input.setSelectionRange(0, 0));
    };

  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLInputElement>) => {
    const input = e.currentTarget;

    if (e.key.length === 1) {
      if (!e.key.match(/[0-9]/)) {
        e.preventDefault();
        return;
      }

      if (
        input.value.length >= input.maxLength &&
        (input.selectionStart || 0) < input.maxLength
      ) {
        input.value = "";
      }

      return;
    }

    if (e.key === "ArrowLeft" && input.selectionStart === 0) {
      moveToPrevious(input);
      e.preventDefault();
      return;
    }

    if (e.key === "ArrowRight" && input.selectionStart === input.maxLength) {
      moveToNext(input);
      e.preventDefault();
      return;
    }
  }, []);

  const handleKeyUp = useCallback((e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") moveToPrevious(e.currentTarget);
  }, []);

  const handleDayChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newDay = e.target.value;
      const newValue = `${year}-${month}-${newDay}`;
      onChange(year || month || newDay ? newValue : "");
      moveToNext(e.target);
    },
    [year, month, onChange],
  );

  const handleMonthChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newMonth = e.target.value;
      const newValue = `${year}-${newMonth}-${day}`;
      onChange(year || newMonth || day ? newValue : "");
      moveToNext(e.target);
    },
    [year, day, onChange],
  );

  const handleYearChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newYear = e.target.value;
      const newValue = `${newYear}-${month}-${day}`;
      onChange(newYear || month || day ? newValue : "");
      moveToNext(e.target);
    },
    [day, month, onChange],
  );

  const handleContainerBlur = () => {
    setFocusedInput(undefined);
  };

  const handleContainerFocus = () => {
    if (onFocus) {
      onFocus();
    }
  };

  const containerRef = useRef<HTMLDivElement | null>(null);
  const focusProps = useFocusWithin(
    containerRef,
    handleContainerFocus,
    handleContainerBlur,
  );

  const containerClasses = clsx(
    "inline-flex",
    "items-center",
    "h-10",
    "p-2",
    "rounded-lg",
    "outline-none",
    "border-none",
    "text-sm",
    "font-normal",
    "font-body",
    focusedInput && !error && "ring-2 ring-stroke-focused",
    error && "ring-2 ring-text-error",
    disabled
      ? "cursor-not-allowed bg-surface-disabled"
      : "bg-surface-tonal-default hover:bg-surface-tonal-bold",
    className,
  );

  const fieldClasses = (fieldType: "day" | "month" | "year") =>
    clsx(
      "appearance-none",
      "m-0",
      "p-0",
      "border-0",
      "bg-transparent",
      "outline-none",
      "placeholder:text-text-tertiary",
      disabled ? "text-text-disabled" : "text-text-primary",
      fieldType === "day" && "min-w-5",
      fieldType === "month" && "min-w-6",
      fieldType === "year" && "min-w-9",
      "[field-sizing:content]",
    );

  return (
    <div className="flex flex-col items-start gap-2">
      <div className="flex flex-col gap-1">
        {label && (
          <label htmlFor={name}>
            <Typo element="p" type="labelMD">
              {label}
            </Typo>
          </label>
        )}
        {subLabel && (
          <Typo element="p" type="bodyXS" textColor="text-text-secondary">
            {subLabel}
          </Typo>
        )}
      </div>

      <div
        ref={containerRef}
        className={containerClasses}
        id={name}
        role="group"
        {...focusProps}
      >
        <input
          ref={(ref) => {
            inputs.current[0] = ref;
          }}
          type="text"
          className={fieldClasses("day")}
          placeholder={dayPlaceholder}
          maxLength={2}
          inputMode="numeric"
          onFocus={handleInputFocus("day")}
          onKeyDown={handleKeyDown}
          onKeyUp={handleKeyUp}
          onChange={handleDayChange}
          value={day}
          disabled={disabled}
          required={required}
        />
        <span className="px-0.5 text-text-disabled">{separator}</span>
        <input
          ref={(ref) => {
            inputs.current[1] = ref;
          }}
          type="text"
          className={fieldClasses("month")}
          placeholder={monthPlaceholder}
          maxLength={2}
          inputMode="numeric"
          onFocus={handleInputFocus("month")}
          onKeyDown={handleKeyDown}
          onKeyUp={handleKeyUp}
          onChange={handleMonthChange}
          value={month}
          disabled={disabled}
          required={required}
        />
        <span className="px-0.5 text-text-disabled">{separator}</span>
        <input
          ref={(ref) => {
            inputs.current[2] = ref;
          }}
          type="text"
          className={fieldClasses("year")}
          placeholder={yearPlaceholder}
          maxLength={4}
          inputMode="numeric"
          onFocus={handleInputFocus("year")}
          onKeyDown={handleKeyDown}
          onKeyUp={handleKeyUp}
          onChange={handleYearChange}
          value={year}
          disabled={disabled}
          required={required}
        />
      </div>

      {error && <FieldError text={error} className="text-text-error" />}
    </div>
  );
};
